import { BlockedTenantReason } from '@reposit/api-client';
import { lowerCase, startCase } from 'lodash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Select from '../../components/FormFields/Select';
import TextArea from '../../components/FormFields/TextArea';
import Modal from '../../components/Modal/index';
import { Header3, P2 } from '../../components/Typography';
import { BLOCK_TENANT_STORE_KEY, blockTenantRequested } from '../../redux/user/user.actions';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';

interface BlockTenantModalProps {
  email: string;
  isLoading: boolean;
  onDismiss: () => void;
}

const ActionBar = styled.div`
  display: flex;
  padding: 24px 0;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 10px;

  button {
    margin-right: 10px;
  }
`;

const BLOCK_TENANT_REASON_PLACEHOLDER = 'SELECT_REASON';

export const BlockTenantModal: React.FC<BlockTenantModalProps> = ({ onDismiss, email, isLoading }) => {
  const dispatch = useDispatch();
  const [reason, setReason] = useState<string>(BLOCK_TENANT_REASON_PLACEHOLDER);
  const [notes, setNotes] = useState('');
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([BLOCK_TENANT_STORE_KEY]);
  const hasSelectedReason = Object.values(BlockedTenantReason).includes(reason as BlockedTenantReason);

  return (
    <Modal onDismiss={onDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3>Block tenant</Header3>
      <P2>{`This will prevent ${email} from being added to new Reposits.`}</P2>
      <FieldWithLabel label="Reason" style={{ marginTop: 16 }}>
        <Select name="reason" onChange={setReason} value={reason}>
          <option value={BLOCK_TENANT_REASON_PLACEHOLDER}>Select a reason</option>
          {Object.values(BlockedTenantReason).map((blockReason) => (
            <option key={blockReason} value={blockReason}>
              {startCase(lowerCase(blockReason))}
            </option>
          ))}
        </Select>
      </FieldWithLabel>
      <FieldWithLabel label="Notes (optional)" style={{ marginTop: 16 }}>
        <TextArea
          name="notes"
          value={notes}
          placeholder="For example, a linked tenant or an outstanding amount"
          onChange={(event) => setNotes(event.target.value)}
          onBlur={() => undefined}
        />
      </FieldWithLabel>
      <ActionBar>
        <Button disabled={isLoading} buttonType="warning" onClick={onDismiss}>
          Cancel
        </Button>
        <Button
          disabled={isLoading || !hasSelectedReason}
          onClick={() =>
            dispatch(blockTenantRequested({ email, reason: reason as BlockedTenantReason, notes: notes.trim() || undefined }))
          }
        >
          Block tenant
        </Button>
      </ActionBar>
    </Modal>
  );
};
